import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Webcam from "react-webcam";
import Keypad from "./Keypad";
import { SALENG_SHOP_API_PROD } from "../config";
import { getAuthorizationHeaders } from "../services/apiClient";
import { useTheme } from "@mui/material/styles";
import ScaleIcon from "@mui/icons-material/Scale";
import CheckIcon from "@mui/icons-material/Check";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle"; // For Light car

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

export default function WeightDialog({
  open,
  onClose,
  onConfirm,
  transaction,
  weighingFlow = "buy",
}) {
  const theme = useTheme();
  const isSaleFlow = weighingFlow === "sale";
  const inboundField = isSaleFlow ? "light" : "heavy";

  const [heavy, setHeavy] = useState("");
  const [light, setLight] = useState("");
  const [focused, setFocused] = useState("heavy"); // "heavy" | "light"
  const [inputValue, setInputValue] = useState("0");
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const webcamRef = useRef(null);

  const [isDisabled, setIsDisabled] = useState(false);
  const [resetNext, setResetNext] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const isCapturingRef = useRef(false);
  const inputValueRef = useRef("0");
  const hasUserEditedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // ---------- helpers ----------
  const display2 = (val) => Number(val || 0).toFixed(2);
  const formatNumber = (num) => {
    if (!num) return "0.00";
    return Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const sanitizeNumber = (s) => {
    if (s == null || s === "") return "0";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x === "" ? "0" : x;
  };

  const netNumber = useMemo(() => {
    const h = parseFloat(heavy || "0");
    const l = parseFloat(light || "0");
    const n = h - l;
    return Number.isNaN(n) ? 0 : n;
  }, [heavy, light]);

  const isNetNegative = netNumber < 0;

  useEffect(() => {
    // We allow manual car weight entry regardless of whether product weighing is done.
    // The dialog's overall accessibility is controlled by parent components (e.g., !isPayed && !isCanceled).
    setIsDisabled(false);
  }, [transaction?.status]);

  // ---------- initialize from transaction once per modal open ----------
  useEffect(() => {
    if (!open) {
      hasUserEditedRef.current = false;
      hasInitializedRef.current = false;
      return;
    }

    if (hasInitializedRef.current) return;

    navigator.mediaDevices.enumerateDevices().then((devs) => {
      const videoInputs = devs.filter((d) => d.kind === "videoinput");
      setDevices(videoInputs);

      const savedId = localStorage.getItem("preferred_camera_car_scale");
      if (savedId && videoInputs.some((d) => d.deviceId === savedId)) {
        setSelectedDeviceId(savedId);
      } else if (videoInputs.length > 0) {
        setSelectedDeviceId(videoInputs[0].deviceId); // default
      }
    });
    setHeavy(
      transaction?.carHeavyWeight != null
        ? String(transaction.carHeavyWeight)
        : "0"
    );
    setLight(
      transaction?.carLightWeight != null
        ? String(transaction.carLightWeight)
        : "0"
    );
    setFocused(inboundField);
    setResetNext(true);
    hasInitializedRef.current = true;
  }, [inboundField, open, transaction]);

  // ---------- sync keypad ตามช่องที่โฟกัส ----------
  useEffect(() => {
    setInputValue(focused === "light" ? light || "0" : heavy || "0");
  }, [focused, heavy, light]);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  // ---------- Handle Physical Keyboard ----------
  useEffect(() => {
    if (!open) {
      setSnapshots([]); // Reset snapshots when dialog closes
      return;
    }

    const isTypingInTextInput = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const isInputLike =
        tag === "input" ||
        tag === "textarea" ||
        el.getAttribute?.("contenteditable") === "true";
      return isInputLike;
    };

    const handleKeyDown = (e) => {
      if (isDisabled) return;
      if (isTypingInTextInput()) return;

      const key = e.key;

      // Numbers
      if (/^[0-9]$/.test(key)) {
        hasUserEditedRef.current = true;
        setInputValue((prev) => {
          if (resetNext) {
            setResetNext(false);
            return key;
          }
          if (prev === "0") return key;
          return prev + key;
        });
        return;
      }

      // Dot
      if (key === ".") {
        hasUserEditedRef.current = true;
        setInputValue((prev) => {
          if (resetNext) {
            setResetNext(false);
            return "0.";
          }
          if (prev.includes(".")) return prev;
          return prev + ".";
        });
        return;
      }

      // Backspace
      if (key === "Backspace") {
        e.preventDefault();
        hasUserEditedRef.current = true;
        setInputValue((prev) => {
          if (prev.length <= 1) return "0";
          return prev.slice(0, -1);
        });
        return;
      }

      // Enter -> OK Logic
      if (key === "Enter") {
        e.preventDefault();
        handleKeypadSubmit(inputValueRef.current);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDisabled, focused, resetNext]);

  // ---------- กล้อง & อัปโหลด ----------
  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return null;
    const video = webcamRef.current.video;
    if (!video || video.readyState < 2) return null;

    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    if (!imageSrc) return null;

    const blob = await (await fetch(imageSrc)).blob();
    return new File([blob], "webcam_photo.jpg", { type: "image/jpeg" });
  }, []);

  async function uploadFile(file) {
    if (!file) throw new Error("No file to upload");
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(SALENG_SHOP_API + "/files", {
      method: "POST",
      headers: getAuthorizationHeaders(),
      body: formData,
    });
    if (!response.ok) throw new Error("Upload failed: " + response.statusText);
    const result = await response.json();
    return result;
  }

  const handleConfirm = async () => {
    onConfirm?.({
      heavy: Number(sanitizeNumber(heavy)),
      light: Number(sanitizeNumber(light)),
      net: netNumber,
      imageUrl: snapshots[snapshots.length - 1]?.url || null, // backward compatibility
      snapshots: snapshots,
    });
  };

  const handleKeypadSubmit = async (result) => {
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;

    try {
      const capturedImage = await captureImage();
      if (capturedImage) {
        const uploadResult = await uploadFile(capturedImage);
        const url = uploadResult?.file;
        if (url) {
          setSnapshots((prev) => [
            ...prev,
            {
              url,
              at: new Date().toISOString(),
              label: focused === "light" ? "car_light" : "car_heavy",
            },
          ]);
        }
      }
    } catch (err) {
      console.log("Error capturing image on keypad OK:", err);
    } finally {
      isCapturingRef.current = false;
    }

    const val = sanitizeNumber(String(result ?? ""));
    hasUserEditedRef.current = false;
    if (focused === "light") {
      setLight(val);
      setFocused("heavy");
    } else {
      setHeavy(val);
      setFocused("light");
    }
    setInputValue("0");
    setResetNext(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          maxHeight: '95vh'
        }
      }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <ScaleIcon className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">
            ชั่งน้ำหนักรถขนส่ง
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
        >
          <CloseIcon />
        </button>
      </div>

      <DialogContent className="!p-6 bg-gray-50/30">
        <div className="grid grid-cols-12 gap-6 items-stretch h-full">

          {/* Left: Webcam */}
          <div className="col-span-12 lg:col-span-4 h-full">
            <div className="h-full min-h-[300px] lg:min-h-0 bg-black rounded-2xl overflow-hidden relative shadow-sm ring-1 ring-gray-200">
              <Webcam
                ref={webcamRef}
                key={selectedDeviceId}
                audio={false}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.9}
                className="w-full h-full object-contain"
                videoConstraints={{
                  width: 1920,
                  height: 1080,
                  deviceId: selectedDeviceId
                    ? { exact: selectedDeviceId }
                    : undefined,
                }}
              />

              {/* Camera Selector Overlay */}
              <div className="absolute top-4 left-4 right-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-1.5 border border-white/20 shadow-lg">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse ml-1" />
                <select
                  value={selectedDeviceId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedDeviceId(val);
                    localStorage.setItem("preferred_camera_car_scale", val);
                  }}
                  className="bg-transparent border-none text-xs font-semibold focus:ring-0 outline-none cursor-pointer flex-1"
                >
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `กล้อง ${d.deviceId}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                REC
              </div>
            </div>
          </div>

          {/* Middle: Inputs */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
            {/* Heavy Weight Card */}
            <div
              onClick={() => {
                setFocused("heavy");
                setInputValue(heavy || "0");
                setResetNext(true);
                hasUserEditedRef.current = false;
              }}
              className={`${isSaleFlow ? "order-2" : "order-1"} p-4 rounded-2xl border-2 transition-all duration-200 bg-white cursor-pointer ${focused === "heavy" ? "border-blue-500 shadow-lg shadow-blue-50" : "border-gray-200 hover:border-blue-200"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <LocalShippingIcon className={focused === 'heavy' ? "text-blue-500" : "text-slate-400"} />
                  <span className={`text-sm font-bold ${focused === 'heavy' ? "text-blue-700" : "text-slate-500"}`}>รถหนัก ({isSaleFlow ? "ขาออก" : "ขาเข้า"})</span>
                </div>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <div className={`text-right w-full text-5xl font-black leading-none ${focused === 'heavy' ? "text-slate-800" : "text-slate-400"}`}>
                  {formatNumber(heavy)}
                </div>
                <span className="text-xl font-bold text-slate-400">กก.</span>
              </div>
            </div>

            {/* Light Weight Card */}
            <div
              onClick={() => {
                setFocused("light");
                setInputValue(light || "0");
                setResetNext(true);
                hasUserEditedRef.current = false;
              }}
              className={`${isSaleFlow ? "order-1" : "order-2"} p-4 rounded-2xl border-2 transition-all duration-200 bg-white cursor-pointer ${focused === "light" ? "border-orange-500 shadow-lg shadow-orange-50" : "border-gray-200 hover:border-orange-200"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AirportShuttleIcon className={focused === 'light' ? "text-orange-500" : "text-slate-400"} />
                  <span className={`text-sm font-bold ${focused === 'light' ? "text-orange-700" : "text-slate-500"}`}>รถเบา ({isSaleFlow ? "ขาเข้า" : "ขาออก"})</span>
                </div>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <div className={`text-right w-full text-5xl font-black leading-none ${focused === 'light' ? "text-slate-800" : "text-slate-400"}`}>
                  {formatNumber(light)}
                </div>
                <span className="text-xl font-bold text-slate-400">กก.</span>
              </div>
            </div>

            {/* Net Scale Summary */}
            <div className="order-3 bg-slate-50 rounded-2xl p-4 border border-slate-200/60">
              <div className="flex justify-between items-center px-2">
                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">น้ำหนักสุทธิ</span>
                <div className="flex items-center gap-2">
                  <span className={`text-4xl font-black ${isNetNegative ? 'text-rose-500' : 'text-emerald-600'}`}>
                    {formatNumber(netNumber)}
                  </span>
                  <span className="text-xl font-bold text-gray-400">กก.</span>
                </div>
              </div>
              {isNetNegative && (
                <div className="text-right px-2 mt-1">
                  <span className="text-xs font-bold text-rose-500 bg-rose-50 px-2 py-1 rounded">น้ำหนักติดลบ (รถเบา มากกว่า รถหนัก)</span>
                </div>
              )}
            </div>

          </div>

          {/* Right: Keypad & Actions */}
          <div className="col-span-12 lg:col-span-3 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col">
            <div className="flex-1">
              <Keypad
                value={inputValue}
                onChange={(val) => setInputValue(val)}
                onClear={() => {
                  setInputValue("0");
                  if (focused === "heavy") setHeavy("0");
                  if (focused === "light") setLight("0");
                }}
                onSubmit={handleKeypadSubmit}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <div className="px-6 py-4 border-t border-gray-100 bg-white sticky bottom-0 z-10 w-full">
        <button
          onClick={handleConfirm}
          className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 active:transform active:scale-[0.98] disabled:bg-gray-200 text-white rounded-2xl shadow-lg shadow-emerald-100 transition-all flex flex-col items-center justify-center gap-0.5"
        >
          <div className="flex items-center gap-2 font-black text-xl">
            <CheckIcon className="!text-2xl" />
            ยืนยันการชั่ง
          </div>
        </button>
      </div>
    </Dialog>
  );
}
